/**
 * Marker for an unpaired utterance in the split-view surface (#2237).
 *
 * Decision in-code: when the source and translation utterance counts
 * differ, each row still renders both cells, but the side with no
 * counterpart shows this empty-state badge in place of the missing
 * cell. We MARK (not pad or merge) because:
 *   - Padding invents data on one side and would read as "we have a
 *     translation for this" when we don't.
 *   - Merging across rows would hide a mismatch that the translator
 *     needs to notice and fix.
 */

import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';

interface Props {
  /** Which side is missing. Drives the message. */
  side: 'source' | 'translation';
}

export function UnpairedRowBadge({ side }: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const message =
    side === 'translation'
      ? t('split-view.no-translation-for-utterance')
      : t('split-view.no-source-for-utterance');
  return (
    <div
      role="note"
      className="rounded-2xl border border-dashed p-3 flex items-center gap-2 text-xs"
      style={{
        borderColor: getColour('border'),
        backgroundColor: getColour('firstLayer'),
        color: getColour('inactiveText')
      }}
    >
      <AlertCircle size={14} aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}
